import { Component, ElementRef, ViewChild, inject, signal, AfterViewInit, OnDestroy } from '@angular/core';
import { TooltipService, TooltipConfig } from '../../services/tooltip.service';
import { Tooltip } from 'flowbite';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-tooltip-container',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tooltip-container.component.html'
})
export class TooltipContainerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('tooltipContent') private contentRef!: ElementRef;
  private tooltipService = inject(TooltipService);
  private configs = new Map<string, TooltipConfig>();
  private tooltips = new Map<string, Tooltip>();

  private addSubscription: Subscription | undefined;
  private removeSubscription: Subscription | undefined;

  public activeConfig = signal<TooltipConfig | null>(null);

  ngAfterViewInit() {
    this.addSubscription = this.tooltipService.added$.subscribe(config => {
      const existingId = config.targetElement.getAttribute('data-tooltip-id');
      if (existingId && this.configs.has(existingId)) {
        this.configs.set(existingId, config);
        return;
      }

      const id = crypto.randomUUID();
      config.targetElement.setAttribute('data-tooltip-id', id);
      this.configs.set(id, config);

      const tooltip = new Tooltip(
        this.contentRef.nativeElement,
        config.targetElement,
        {
          placement: config.placement,
          onShow: () => {
            const tooltipId = config.targetElement.getAttribute('data-tooltip-id');
            if (tooltipId && this.configs.has(tooltipId)) {
              this.activeConfig.set(this.configs.get(tooltipId) ?? null);
            }
          }
        },
        { id }
      );

      this.tooltips.set(id, tooltip);
    });

    this.removeSubscription = this.tooltipService.removed$.subscribe(element => {
      const id = element.getAttribute('data-tooltip-id');
      if (id) {
        // Ensure the tooltip is hidden
        this.tooltips.get(id)?.hide();

        // Destroy tooltip instance
        this.tooltips.get(id)?.destroyAndRemoveInstance();
        this.tooltips.delete(id);

        // Remove config
        this.configs.delete(id);

        // Clear active config if this was the active tooltip
        if (this.activeConfig()?.targetElement === element) {
          this.activeConfig.set(null);
        }

        // Remove the data attribute
        element.removeAttribute('data-tooltip-id');
      }
    });
  }

  ngOnDestroy() {
    // Clean up all tooltips
    this.tooltips.forEach(tooltip => tooltip.destroyAndRemoveInstance());
    this.tooltips.clear();
    this.configs.clear();

    // Unsubscribe from subscriptions
    this.addSubscription?.unsubscribe();
    this.removeSubscription?.unsubscribe();
  }
}
